import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useAlisonRun } from '../AlisonRunProvider.js';
import { builtRows, fillTemplate } from '../data/alisonRun.js';
import { DrawerOutcomeCard } from './AlisonRunDrawer.js';
import { ALISON_BORDER_GRADIENT } from './alisonRunColors.js';

// Shown over the workflow once Alison finishes: what she saved, and the page to go on from.
export function AlisonDoneBanner(): ReactNode {
  const { rows, copy, peek, closeResults } = useAlisonRun();
  const navigate = useNavigate();
  const last = builtRows(rows).at(-1);
  const outcome = last?.title ?? '';
  const outcomes = rows.flatMap((row) =>
    row.outcome === null ? [] : [{ actionType: row.actionType, outcome: row.outcome }],
  );
  return (
    <Stack
      role="status"
      aria-label={copy.runComplete}
      sx={{
        gap: '12px',
        mx: '24px',
        mt: '16px',
        p: '16px',
        border: '1px solid transparent',
        borderRadius: '16px',
        background: (theme) =>
          `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}) padding-box, ${ALISON_BORDER_GRADIENT} border-box`,
      }}
    >
      <Stack direction="row" sx={{ gap: '12px', alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: 9,
            height: 9,
            mt: '5px',
            flexShrink: 0,
            borderRadius: '50%',
            bgcolor: 'success.main',
          }}
        />
        <Stack sx={{ flex: 1, minWidth: 0, gap: '4px' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'background.icon' }}>
            {last?.itemCount == null || last.itemCount <= 1
              ? fillTemplate(copy.doneHeadline, { outcome })
              : fillTemplate(copy.doneBanner, {
                  count: last.itemCount,
                  outcome: outcome.toLowerCase(),
                })}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.muted' }}>{copy.doneBannerBody}</Typography>
        </Stack>
        <Stack direction="row" sx={{ gap: '8px', flexShrink: 0 }}>
          <Button
            size="small"
            onClick={() =>
              void navigate({
                to: '/outcomes',
                search: last?.runId == null ? {} : { openRunId: last.runId },
                ignoreBlocker: true,
              })
            }
          >
            {copy.viewOutcomes}
          </Button>
          <Button size="small" onClick={closeResults}>
            {copy.close}
          </Button>
        </Stack>
      </Stack>
      {outcomes.length > 0 && (
        <Stack direction="row" sx={{ gap: '10px', flexWrap: 'wrap', pl: '21px' }}>
          {outcomes.map((entry) => (
            <DrawerOutcomeCard
              key={entry.outcome.runId}
              outcome={entry.outcome}
              onOpen={() => peek(entry.actionType)}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
